import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  LogOut, Plus, Trash2, Users, Palette, 
  Loader2, X, ExternalLink, Mail, Phone, Pencil, Upload, Image as ImageIcon
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import api from "@/lib/api";

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [wraps, setWraps] = useState([]);
  const [leads, setLeads] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showWrapForm, setShowWrapForm] = useState(false);
  const [editingWrap, setEditingWrap] = useState(null);
  const [activeTab, setActiveTab] = useState("wraps");
  const [wrapForm, setWrapForm] = useState({
    name: "",
    category: "solid",
    color_code: "#000000",
    thumbnail_url: "",
    description: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [useUrlInput, setUseUrlInput] = useState(false);
  const fileInputRef = useRef(null);

  const token = localStorage.getItem("admin_token");
  const username = localStorage.getItem("admin_username");

  useEffect(() => {
    if (!token) {
      navigate("/admin");
      return;
    }
    fetchData();
  }, [token, navigate]);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [wrapsRes, leadsRes] = await Promise.all([
        api.get("/wraps"),
        api.get("/leads")
      ]);
      setWraps(wrapsRes.data);
      setLeads(leadsRes.data);
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem("admin_token");
        localStorage.removeItem("admin_username");
        navigate("/admin");
      } else {
        toast.error("Failed to load data");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setWrapForm({
      name: "",
      category: "solid",
      color_code: "#000000",
      thumbnail_url: "",
      description: ""
    });
    setEditingWrap(null);
    setShowWrapForm(false);
    setImagePreview(null);
    setUseUrlInput(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const compressImage = (file, maxSizeMB = 5) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new window.Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let { width, height } = img;
          
          const maxDimension = 1920;
          if (width > maxDimension || height > maxDimension) {
            if (width > height) {
              height = (height / width) * maxDimension;
              width = maxDimension;
            } else {
              width = (width / height) * maxDimension;
              height = maxDimension;
            }
          }
          
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          
          let quality = 0.9;
          let result = canvas.toDataURL('image/jpeg', quality);
          
          while (result.length > maxSizeMB * 1024 * 1024 * 1.37 && quality > 0.1) {
            quality -= 0.1;
            result = canvas.toDataURL('image/jpeg', quality);
          }
          
          resolve(result);
        };
        img.src = e.target?.result;
      };
      reader.readAsDataURL(file);
    });
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error("Please upload an image file");
      return;
    }

    try {
      let base64;
      if (file.size > 5 * 1024 * 1024) {
        toast.info("Compressing large image...");
        base64 = await compressImage(file, 5);
        toast.success("Image compressed successfully");
      } else {
        base64 = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onload = (event) => resolve(event.target?.result);
          reader.readAsDataURL(file);
        });
      }
      
      setImagePreview(base64);
      setWrapForm(prev => ({ ...prev, thumbnail_url: base64 }));
    } catch (error) {
      toast.error("Failed to process image");
    }
  };

  const openEditForm = (wrap) => {
    setEditingWrap(wrap);
    setWrapForm({
      name: wrap.name,
      category: wrap.category,
      color_code: wrap.color_code || "#000000",
      thumbnail_url: wrap.thumbnail_url || "",
      description: wrap.description || ""
    });
    setImagePreview(wrap.thumbnail_url || null);
    setUseUrlInput(wrap.thumbnail_url?.startsWith('http') || false);
    setShowWrapForm(true);
  };

  const openAddForm = () => {
    resetForm();
    setShowWrapForm(true);
  };

  const handleSubmitWrap = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      if (editingWrap) {
        await api.put(`/wraps/${editingWrap.id}`, wrapForm);
        toast.success("Wrap updated successfully");
      } else {
        await api.post("/wraps", wrapForm);
        toast.success("Wrap added successfully");
      }
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(editingWrap ? "Failed to update wrap" : "Failed to add wrap");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteWrap = async (wrapId) => {
    if (!window.confirm("Are you sure you want to delete this wrap?")) return;

    try {
      await api.delete(`/wraps/${wrapId}`);
      toast.success("Wrap deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete wrap");
    }
  };

  const handleDeleteLead = async (leadId) => {
    if (!window.confirm("Are you sure you want to delete this lead?")) return;

    try {
      await api.delete(`/leads/${leadId}`);
      toast.success("Lead deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete lead");
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    localStorage.removeItem("admin_username");
    navigate("/admin");
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-premier-navy flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-premier-blue animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-premier-navy">
      {/* Header */}
      <header className="sticky top-0 z-50 glass-heavy px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="font-unbounded text-lg font-bold tracking-tight">
            <img src="/logo.png" alt="Premier Signs & Wraps" className="h-10" />
            <span className="font-mono text-xs text-white/40 ml-3">Admin</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="font-mono text-xs text-white/60">
              Welcome, {username}
            </span>
            <Button
              variant="ghost"
              className="text-white/60 hover:text-premier-blue"
              onClick={handleLogout}
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <StatCard 
            icon={<Palette className="w-6 h-6" />}
            label="Total Wraps"
            value={wraps.length}
          />
          <StatCard 
            icon={<Users className="w-6 h-6" />}
            label="Total Leads"
            value={leads.length}
          />
          <StatCard 
            icon={<Palette className="w-6 h-6" />}
            label="Solid Colours"
            value={wraps.filter(w => w.category === 'solid').length}
          />
          <StatCard 
            icon={<Palette className="w-6 h-6" />}
            label="Patterns"
            value={wraps.filter(w => w.category === 'pattern').length}
          />
        </div>

        {/* Simple Tab Buttons */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setActiveTab("wraps")}
            className={`flex items-center gap-2 px-6 py-3 rounded font-manrope font-semibold transition-all ${
              activeTab === "wraps" 
                ? "bg-premier-blue text-white" 
                : "bg-premier-purple text-white/60 hover:text-white border border-white/10"
            }`}
            data-testid="wraps-tab"
          >
            <Palette className="w-4 h-4" />
            Wraps
          </button>
          <button
            onClick={() => setActiveTab("leads")}
            className={`flex items-center gap-2 px-6 py-3 rounded font-manrope font-semibold transition-all ${
              activeTab === "leads" 
                ? "bg-premier-blue text-white" 
                : "bg-premier-purple text-white/60 hover:text-white border border-white/10"
            }`}
            data-testid="leads-tab"
          >
            <Users className="w-4 h-4" />
            Leads
          </button>
        </div>

        {/* Wraps Tab */}
        {activeTab === "wraps" && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h2 className="font-unbounded font-bold text-xl">Manage Wraps</h2>
              <Button
                onClick={openAddForm}
                className="btn-primary"
                data-testid="add-wrap-btn"
              >
                <Plus className="w-5 h-5 mr-2" />
                Add Wrap
              </Button>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {wraps.map((wrap) => (
                <motion.div
                  key={wrap.id}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="group relative aspect-square rounded-lg overflow-hidden border border-white/10 hover:border-premier-blue/50 transition-colors"
                >
                  <img 
                    src={wrap.thumbnail_url} 
                    alt={wrap.name}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-3">
                    <p className="font-manrope font-semibold text-sm">{wrap.name}</p>
                    <p className="font-mono text-xs text-white/40 uppercase">{wrap.category}</p>
                  </div>
                  <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-all">
                    <button
                      onClick={() => openEditForm(wrap)}
                      className="p-2 bg-black/50 rounded-full hover:bg-blue-500 transition-all"
                      data-testid={`edit-wrap-${wrap.id}`}
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteWrap(wrap.id)}
                      className="p-2 bg-black/50 rounded-full hover:bg-premier-blue transition-all"
                      data-testid={`delete-wrap-${wrap.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        )}

        {/* Leads Tab */}
        {activeTab === "leads" && (
          <div>
            <h2 className="font-unbounded font-bold text-xl mb-6">Customer Leads</h2>
            
            {leads.length === 0 ? (
              <div className="text-center py-12 glass rounded-lg">
                <Users className="w-12 h-12 text-white/20 mx-auto mb-4" />
                <p className="text-white/60">No leads yet</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Contact</th>
                      <th>Budget</th>
                      <th>Date</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {leads.map((lead) => (
                      <tr key={lead.id}>
                        <td className="font-manrope font-semibold">{lead.name}</td>
                        <td>
                          <div className="flex flex-col gap-1">
                            <a 
                              href={`mailto:${lead.email}`}
                              className="flex items-center gap-2 text-sm text-white/60 hover:text-premier-blue"
                            >
                              <Mail className="w-4 h-4" />
                              {lead.email}
                            </a>
                            <a 
                              href={`tel:${lead.phone}`}
                              className="flex items-center gap-2 text-sm text-white/60 hover:text-premier-blue"
                            >
                              <Phone className="w-4 h-4" />
                              {lead.phone}
                            </a>
                          </div>
                        </td>
                        <td className="font-mono text-premier-blue">{lead.budget}</td>
                        <td className="font-mono text-xs text-white/40">
                          {new Date(lead.created_at).toLocaleDateString()}
                        </td>
                        <td>
                          <div className="flex gap-2">
                            {lead.generated_image_url && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => window.open(lead.generated_image_url, '_blank')}
                                className="text-white/60 hover:text-premier-blue"
                              >
                                <ExternalLink className="w-4 h-4" />
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteLead(lead.id)}
                              className="text-white/60 hover:text-premier-blue"
                              data-testid={`delete-lead-${lead.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </main>

      {/* Add/Edit Wrap Modal */}
      {showWrapForm && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-6 bg-black/80"
          onClick={resetForm}
        >
          <div
            className="glass-heavy w-full max-w-md p-8 rounded-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-6">
              <h3 className="font-unbounded font-bold text-xl">
                {editingWrap ? "Edit Wrap" : "Add New Wrap"}
              </h3>
              <button 
                onClick={resetForm}
                className="text-white/60 hover:text-white"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmitWrap} className="space-y-4">
              <div>
                <Label className="form-label">Name</Label>
                <Input
                  required
                  value={wrapForm.name}
                  onChange={(e) => setWrapForm({ ...wrapForm, name: e.target.value })}
                  placeholder="e.g., Satin Red"
                  className="h-12"
                  data-testid="wrap-name-input"
                />
              </div>

              <div>
                <Label className="form-label">Category</Label>
                <select
                  value={wrapForm.category}
                  onChange={(e) => setWrapForm({ ...wrapForm, category: e.target.value })}
                  className="w-full h-12 px-3 rounded-md bg-white/5 border border-white/10 text-white focus:border-premier-blue focus:outline-none"
                  data-testid="wrap-category-select"
                >
                  <option value="solid" className="bg-premier-purple">Solid Colour</option>
                  <option value="pattern" className="bg-premier-purple">Pattern</option>
                </select>
              </div>

              {wrapForm.category === 'solid' && (
                <div>
                  <Label className="form-label">Colour Code</Label>
                  <div className="flex gap-2">
                    <input
                      type="color"
                      value={wrapForm.color_code}
                      onChange={(e) => setWrapForm({ ...wrapForm, color_code: e.target.value })}
                      className="w-12 h-12 rounded cursor-pointer"
                    />
                    <Input
                      value={wrapForm.color_code}
                      onChange={(e) => setWrapForm({ ...wrapForm, color_code: e.target.value })}
                      placeholder="#000000"
                      className="h-12 flex-1"
                      data-testid="wrap-color-input"
                    />
                  </div>
                </div>
              )}

              <div>
                <Label className="form-label">Wrap Image</Label>
                <div className="flex gap-2 mb-3">
                  <button
                    type="button"
                    onClick={() => setUseUrlInput(false)}
                    className={`flex-1 py-2 px-3 rounded text-sm font-medium transition-all ${
                      !useUrlInput 
                        ? "bg-premier-blue text-white" 
                        : "bg-white/5 text-white/60 hover:text-white border border-white/10"
                    }`}
                  >
                    <Upload className="w-4 h-4 inline mr-2" />
                    Upload
                  </button>
                  <button
                    type="button"
                    onClick={() => setUseUrlInput(true)}
                    className={`flex-1 py-2 px-3 rounded text-sm font-medium transition-all ${
                      useUrlInput 
                        ? "bg-premier-blue text-white" 
                        : "bg-white/5 text-white/60 hover:text-white border border-white/10"
                    }`}
                  >
                    <ImageIcon className="w-4 h-4 inline mr-2" />
                    URL
                  </button>
                </div>
                
                {useUrlInput ? (
                  <Input
                    required={!imagePreview}
                    value={wrapForm.thumbnail_url?.startsWith('data:') ? '' : wrapForm.thumbnail_url}
                    onChange={(e) => {
                      setWrapForm({ ...wrapForm, thumbnail_url: e.target.value });
                      setImagePreview(e.target.value);
                    }}
                    placeholder="https://images.unsplash.com/..."
                    className="h-12"
                    data-testid="wrap-thumbnail-input"
                  />
                ) : (
                  <div 
                    className="border-2 border-dashed border-white/20 rounded-lg p-4 text-center cursor-pointer hover:border-premier-blue/50 transition-colors"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="hidden"
                      data-testid="wrap-image-upload"
                    />
                    {imagePreview ? (
                      <div className="relative">
                        <img 
                          src={imagePreview} 
                          alt="Preview" 
                          className="max-h-32 mx-auto rounded object-cover"
                        />
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setImagePreview(null);
                            setWrapForm(prev => ({ ...prev, thumbnail_url: "" }));
                            if (fileInputRef.current) fileInputRef.current.value = "";
                          }}
                          className="absolute -top-2 -right-2 p-1 bg-premier-blue rounded-full hover:bg-red-600 transition-colors"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <div className="text-white/40">
                        <Upload className="w-8 h-8 mx-auto mb-2" />
                        <p className="text-sm">Click to upload image</p>
                        <p className="text-xs mt-1">PNG, JPG up to 5MB</p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div>
                <Label className="form-label">Description</Label>
                <Input
                  value={wrapForm.description}
                  onChange={(e) => setWrapForm({ ...wrapForm, description: e.target.value })}
                  placeholder="Brief description of the wrap..."
                  className="h-12"
                  data-testid="wrap-description-input"
                />
              </div>

              <div className="flex gap-4 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1 btn-secondary"
                  onClick={resetForm}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 btn-primary"
                  data-testid="submit-wrap-btn"
                >
                  {isSubmitting ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    editingWrap ? "Save Changes" : "Add Wrap"
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

const StatCard = ({ icon, label, value }) => (
  <div className="glass p-4 rounded-lg">
    <div className="flex items-center gap-3">
      <div className="text-premier-blue">{icon}</div>
      <div>
        <p className="font-mono text-xs text-white/40 uppercase">{label}</p>
        <p className="font-unbounded font-bold text-2xl">{value}</p>
      </div>
    </div>
  </div>
);

export default AdminDashboard;
